import {AxiosResponse} from 'axios';
import {axiosInstance} from '..';


export interface ScheduleItem {
  date: string;
  start_time: string;
  end_time: string;
  queue_supplier: number;
}

export interface ScheduleRequest {
  Schedules: ScheduleItem[];
  QueueSupplier: number;
}

export interface UpdateScheduleStatusRequest {
  scheduleIds: number[];
  action: 'block' | 'unblock' | 'softdelete';
}

export function registerSchedule(request: ScheduleRequest): Promise<AxiosResponse<any, any>> {
  return axiosInstance.post(`Schedule/Register/`, request, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

export function getSchedules(date, startDate, endDate) {
  const params = new URLSearchParams();

  if (date) {
    params.append('date', date);
  } else if (startDate && endDate) {
    params.append('startDate', startDate);
    params.append('endDate', endDate);
  }

  const url = `Schedule/GetSchedulesByDate?${params.toString()}`;
  
  return axiosInstance.get(`Schedule/GetSchedulesByDate?${params.toString()}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
}

export function updateScheduleStatus(request: UpdateScheduleStatusRequest): Promise<AxiosResponse<any, any>> {
  return axiosInstance.put(`Schedule/ActionSchedule/`, request, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

export interface UpdateScheduleItem {
  id: number;
  startTime: string;
  endTime: string;
  queue_supplier: number;
}

export interface UpdateSchedulesRequest {
  Schedules: UpdateScheduleItem[];
}

export function updateSchedules(request: UpdateSchedulesRequest): Promise<AxiosResponse<any, any>> {
  return axiosInstance.put(`Schedule/UpdateSchedules/`, request, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

export function getPositionsPerOrder(supplierId, searchTerm = "", page = 1, pageSize = 5) {
  const params = new URLSearchParams({
    supplierId: supplierId.toString(),
    page: page.toString(),
    pageSize: pageSize.toString()
  });

  if (searchTerm.trim() !== "") {
    params.append('searchTerm', searchTerm);
  }

  return axiosInstance.get(`Appointment/GetPositionsPerOrder?${params.toString()}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
}

export function createAppointment(requestData) {
  return axiosInstance.post(`Appointment/Create/`, requestData, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

export function addNewSchedules(request: ScheduleRequest): Promise<AxiosResponse<any, any>> {
  return axiosInstance.post(`Schedule/AddNewSchedules/`, request, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

export function getSchedulesWithAppointments(date: string): Promise<AxiosResponse<any, any>> {
  return axiosInstance.get(`Schedule/SchedulesWithAppointments/${date}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
}

export function getUnconfirmedAppointments(
  date?: string,
  startTime?: string,
  endTime?: string
): Promise<AxiosResponse<any, any>> {
  const params = new URLSearchParams();
  
  if (date) {
    params.append('date', date);
  }
  
  if (startTime && endTime) {
    params.append('startTime', startTime);
    params.append('endTime', endTime);
  }
  
  return axiosInstance.get(`Appointment/ListUnconfirmedAppointments?${params.toString()}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
}

export function getScheduledAppointments(
  searchTerm?: string,
  date?: string,
  startTime?: string,
  endTime?: string,
  page: number = 1,
  pageSize: number = 10
): Promise<AxiosResponse<any, any>> {
  const params = new URLSearchParams();
  
  if (searchTerm && searchTerm.trim() !== "") {
    params.append('searchTerm', searchTerm);
  }
  
  if (date) {
    params.append('date', date);
  }
  
  if (startTime) {
    params.append('startTime', startTime);
  }
  
  if (endTime) {
    params.append('endTime', endTime);
  }
  
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  
  return axiosInstance.get(`Appointment/ListScheduledAppointments?${params.toString()}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
}

export function downloadExcelAppointments(
  searchTerm?: string,
  date?: string,
  startTime?: string,
  endTime?: string,
  startDate?: string,  
  endDate?: string  
): Promise<AxiosResponse<any, any>> {
  const params = new URLSearchParams();
  
  if (searchTerm && searchTerm.trim() !== "") {
    params.append('searchTerm', searchTerm);
  }
  
  if (date) {
    params.append('date', date);
  }
  
  if (startTime) {
    params.append('startTime', startTime);
  }
  
  if (endTime) {
    params.append('endTime', endTime);
  }

  if (startDate) {
    params.append('startDate', startDate);
  }
  
  if (endDate) {
    params.append('endDate', endDate);
  }
  
  return axiosInstance.get(`Appointment/ExcelAppointments?${params.toString()}`, {
    responseType: 'blob'
  });
}

export function updateAppointmentStatus(appointmentId, requestData) {
  return axiosInstance.put(`Appointment/UpdateAppointmentStatus/${appointmentId}`, requestData, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

export function updateDeliveryStatus(positionId, delivered, appointmentId) {
  return axiosInstance.put(`Appointment/DeliveredPosition/${positionId}`, {
    delivered: delivered,
    appointmentId: appointmentId
  }, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

export interface SettingResponse {
  message: string;
  search_method: string;
  setting: {
    id: number;
    key_name: string;
    key_value: string | null;
    created_At: string;
    updated_At: string | null;
  };
}

export interface UpdateSettingRequest {
  key_value: string;
}

export function getSetting(id: number): Promise<AxiosResponse<SettingResponse, any>> {
  return axiosInstance.get(`Settings/GetSetting?id=${id}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
}

export function updateSetting(settingId: number, request: UpdateSettingRequest): Promise<AxiosResponse<any, any>> {
  return axiosInstance.put(`Settings/AddKeyValue/${settingId}`, request, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}